/**
 * The web app's last line, and the desktop shell's.
 *
 * The native app got one of these first, after a TestFlight build crashed on
 * sign-in and then on every launch with no message anywhere. The browser is
 * kinder — an unhandled render error unmounts the tree rather than killing the
 * process — but the result a user sees is a blank page, which is worse to
 * report than a crash because there is nothing to describe.
 *
 * Unlike the native one this does NOT offer to sign out. A browser tab has no
 * equivalent of the relaunch loop that made that button necessary: reloading
 * lands on a fresh render, and the session lives in an httpOnly cookie the
 * page cannot clear anyway. Reload and a link out are the two useful actions.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { crashReporter } from '../lib/reporting.js';

interface Props {
  readonly children: ReactNode;
}

interface State {
  readonly error: Error | null;
  readonly componentStack: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ componentStack: info.componentStack ?? null });
    // `render` is the kind, not `unhandled`: a render error and a stray
    // `setTimeout` throw are different bugs with different fixes, and the
    // report list separates them.
    void crashReporter().report(error, 'render');
  }

  override render(): ReactNode {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="crash">
        <p className="crash-eyebrow">SOMETHING BROKE</p>
        <h1 className="crash-title">AEGIS hit an error it could not recover from.</h1>
        <p className="crash-body">
          This is a bug in the app, not something you did. It has been reported.
          Reloading usually works — the error is in this screen, not in your data.
        </p>

        <div className="crash-actions">
          <button type="button" className="crash-button" onClick={() => { location.reload(); }}>
            Reload
          </button>
          <a className="crash-link" href="/app/">Back to Today</a>
        </div>

        <details className="crash-details">
          <summary>Technical detail</summary>
          <p className="crash-label">ERROR</p>
          <pre className="crash-pre">{error.name}: {error.message}</pre>
          {error.stack ? (
            <>
              <p className="crash-label">STACK</p>
              <pre className="crash-pre crash-pre-small">{error.stack}</pre>
            </>
          ) : null}
          {componentStack ? (
            <>
              <p className="crash-label">COMPONENTS</p>
              <pre className="crash-pre crash-pre-small">{componentStack.trim()}</pre>
            </>
          ) : null}
        </details>
      </div>
    );
  }
}
